/**
 * OSS 上传 Hook — 封装 presigned URL + 直传 OSS 的完整流程
 *
 * 职责：
 *   - 并行上传多个文件到 OSS
 *   - 提供每个文件的上传进度回调
 *   - 返回 storage_path + download_url
 *
 * 复用基础设施: attachmentApi.uploadFile (presigned + PUT)
 */

import { useCallback, useRef } from "react";
import { uploadFile } from "../../../../../api/attachmentApi";

/**
 * @returns {{
 *   upload: (file: File, onProgress: (percent: number) => void) => Promise<UploadResult>,
 *   uploadBatch: (items: Array<{id, file, onProgress}>) => Promise<Map<string, UploadResult>>,
 *   cancelAll: () => void,
 * }}
 *
 * UploadResult = { storage_path, filename, content_type, file_size, download_url }
 */
export function useOssUploader() {
    const abortRef = useRef(false);

    /**
     * 上传单个文件
     */
    const upload = useCallback(async (file, onProgress) => {
        const result = await uploadFile(file, onProgress, true);
        return {
            storage_path: result.storage_path,
            filename: result.filename || file.name,
            content_type: result.content_type || file.type,
            file_size: result.file_size || file.size,
            download_url: result.download_url,
        };
    }, []);

    /**
     * 并行上传一批文件
     *
     * @param {Array<{id: string, file: File, onProgress: (percent: number) => void}>} items
     * @returns {Promise<Map<string, UploadResult|Error>>}
     */
    const uploadBatch = useCallback(async (items) => {
        abortRef.current = false;
        const results = new Map();

        // 并行上传（受浏览器并发限制，通常 6 个并行）
        const promises = items.map(async ({ id, file, onProgress }) => {
            if (abortRef.current) {
                results.set(id, new Error("已取消"));
                return;
            }
            try {
                const result = await upload(file, onProgress);
                results.set(id, result);
            } catch (err) {
                results.set(id, err);
            }
        });

        await Promise.allSettled(promises);
        return results;
    }, [upload]);

    /**
     * 取消所有正在进行的上传
     */
    const cancelAll = useCallback(() => {
        abortRef.current = true;
    }, []);

    return { upload, uploadBatch, cancelAll };
}
